/**
 * AIF-01 — AI Model DETAIL page (`/ai-models/:modelId`, §11.2).
 *
 * Header (name, type, active, description; Edit/Delete for writers) + a
 * **Versions** subtable (number, name, released, active, artifact; ＋ Add;
 * row overflow Edit / Deactivate-Activate / Delete) with the highest ACTIVE
 * version marked "latest", and a **Classes** subtable sorted by index (Idx,
 * class_name, Display name (en), Active, Sort; ＋ Add; ⇪ Import; row overflow
 * Edit / Delete). Read-only personas see no write affordances (AC4).
 */
import { useMemo, useState, type ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type {
  AiFindingModelClass,
  AiFindingModelVersion,
} from "@aeriosiq/types";
import {
  useAiModelQuery,
  useDeleteAiModelClassMutation,
  useDeleteAiModelVersionMutation,
  useUpdateAiModelVersionMutation,
} from "./aiModelQueries.js";
import { useAiModelWritePermissions } from "./useAiModelWritePermissions.js";
import { modelTypeLabel } from "./modelType.js";
import { StatusDot } from "./StatusDot.js";
import { EditModelDialog } from "./EditModelDialog.js";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.js";
import { VersionDialog } from "./VersionDialog.js";
import { ClassDialog, classRoleLabel } from "./ClassDialog.js";
import { ImportClassesDialog } from "./ImportClassesDialog.js";
import { useDeleteAiModelMutation } from "./aiModelQueries.js";
import {
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  FileUpIcon,
  PlusIcon,
} from "@aeriosiq/ui/icons";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

export function AiModelDetailPage(): ReactElement {
  const { modelId = "" } = useParams<{ modelId: string }>();
  const navigate = useNavigate();
  const query = useAiModelQuery(modelId);
  const { canWrite } = useAiModelWritePermissions();

  const [editModelOpen, setEditModelOpen] = useState(false);
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const deleteModel = useDeleteAiModelMutation();

  // Versions.
  const [versionDialog, setVersionDialog] = useState<{
    version: AiFindingModelVersion | null;
  } | null>(null);
  const [deleteVersion, setDeleteVersion] =
    useState<AiFindingModelVersion | null>(null);
  const [verMenuAnchor, setVerMenuAnchor] = useState<HTMLElement | null>(null);
  const [verMenuTarget, setVerMenuTarget] =
    useState<AiFindingModelVersion | null>(null);
  const updateVersion = useUpdateAiModelVersionMutation(modelId);
  const deleteVersionMut = useDeleteAiModelVersionMutation(modelId);

  // Classes.
  const [classDialog, setClassDialog] = useState<{
    klass: AiFindingModelClass | null;
  } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteClass, setDeleteClass] = useState<AiFindingModelClass | null>(
    null,
  );
  const [clsMenuAnchor, setClsMenuAnchor] = useState<HTMLElement | null>(null);
  const [clsMenuTarget, setClsMenuTarget] =
    useState<AiFindingModelClass | null>(null);
  const deleteClassMut = useDeleteAiModelClassMutation(modelId);

  const data = query.data;
  const versions = useMemo(
    () =>
      [...(data?.versions ?? [])].sort(
        (a, b) => b.versionNumber - a.versionNumber,
      ),
    [data],
  );
  const classes = useMemo(
    () =>
      [...(data?.classes ?? [])].sort((a, b) => a.classIndex - b.classIndex),
    [data],
  );

  // "latest" = the highest ACTIVE version number.
  const latestActive = useMemo(
    () =>
      versions
        .filter((v) => v.isActive)
        .reduce<
          number | null
        >((max, v) => (max === null ? v.versionNumber : Math.max(max, v.versionNumber)), null),
    [versions],
  );

  const nextVersionNumber = useMemo(
    () => versions.reduce((max, v) => Math.max(max, v.versionNumber), 0) + 1,
    [versions],
  );
  const nextClassIndex = useMemo(
    () => classes.reduce((max, c) => Math.max(max, c.classIndex + 1), 0),
    [classes],
  );

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress aria-label="Loading model" />
      </Box>
    );
  }
  if (query.isError || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Couldn&apos;t load the AI model.</Typography>
      </Box>
    );
  }

  const model = data.model;

  return (
    <Box sx={{ height: "100%", overflowY: "auto", pb: 6 }}>
      <MuiLink
        component="button"
        onClick={() => {
          void navigate("/ai-models");
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
        underline="hover"
      >
        <ArrowLeftIcon fontSize="small" /> AI Models
      </MuiLink>

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            {model.modelName}
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {modelTypeLabel(model.modelType)}
            </Typography>
            <StatusDot active={model.isActive} />
            <Typography variant="body2" color="text.secondary">
              {model.isActive ? "Active" : "Inactive"}
            </Typography>
          </Stack>
          {model.description ? (
            <Typography variant="body2" sx={{ mt: 1, maxWidth: 640 }}>
              {model.description}
            </Typography>
          ) : null}
        </Box>
        {canWrite ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => {
                setEditModelOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteModelOpen(true);
              }}
            >
              Delete
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {/* Versions */}
      <Paper variant="outlined" sx={{ mt: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="subtitle1">Versions</Typography>
          {canWrite ? (
            <Button
              size="small"
              startIcon={<PlusIcon fontSize="small" />}
              aria-label="Add version"
              onClick={() => {
                setVersionDialog({ version: null });
              }}
            >
              Add version
            </Button>
          ) : null}
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Versions">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Released</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell>Artifact</TableCell>
                <TableCell />
                {canWrite ? <TableCell padding="checkbox" /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 7 : 6}>
                    <Typography variant="body2" color="text.secondary">
                      No versions yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                versions.map((v) => (
                  <TableRow key={v.versionNumber}>
                    <TableCell>{v.versionNumber}</TableCell>
                    <TableCell>{v.versionName ?? "—"}</TableCell>
                    <TableCell>{fmtDate(v.releaseDate)}</TableCell>
                    <TableCell align="center">
                      <StatusDot active={v.isActive} />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v.artifactUri ?? "—"}
                    </TableCell>
                    <TableCell>
                      {latestActive !== null &&
                      v.versionNumber === latestActive ? (
                        <Chip size="small" color="primary" label="latest" />
                      ) : null}
                    </TableCell>
                    {canWrite ? (
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={`More actions for version ${String(v.versionNumber)}`}
                          onClick={(e) => {
                            setVerMenuAnchor(e.currentTarget);
                            setVerMenuTarget(v);
                          }}
                        >
                          <EllipsisVerticalIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Classes */}
      <Paper variant="outlined" sx={{ mt: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="subtitle1">
            Classes ({classes.length})
          </Typography>
          {canWrite ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<FileUpIcon fontSize="small" />}
                aria-label="Import classes"
                onClick={() => {
                  setImportOpen(true);
                }}
              >
                Import classes.txt / data.yaml
              </Button>
              <Button
                size="small"
                startIcon={<PlusIcon fontSize="small" />}
                aria-label="Add class"
                onClick={() => {
                  setClassDialog({ klass: null });
                }}
              >
                Add
              </Button>
            </Stack>
          ) : null}
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Classes">
            <TableHead>
              <TableRow>
                <TableCell>Idx</TableCell>
                <TableCell>class_name</TableCell>
                <TableCell>Display name (en)</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Sort</TableCell>
                {canWrite ? <TableCell padding="checkbox" /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 7 : 6}>
                    <Typography variant="body2" color="text.secondary">
                      No classes yet. Import a classes.txt / data.yaml or add
                      one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                classes.map((c) => (
                  <TableRow key={c.classUuid}>
                    <TableCell>{c.classIndex}</TableCell>
                    <TableCell>{c.className}</TableCell>
                    <TableCell>{c.displayName?.en ?? "—"}</TableCell>
                    <TableCell>{classRoleLabel(c.classRole)}</TableCell>
                    <TableCell align="center">
                      <StatusDot active={c.isActive} />
                    </TableCell>
                    <TableCell align="right">{c.sortOrder}</TableCell>
                    {canWrite ? (
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={`More actions for class ${c.className}`}
                          onClick={(e) => {
                            setClsMenuAnchor(e.currentTarget);
                            setClsMenuTarget(c);
                          }}
                        >
                          <EllipsisVerticalIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Version overflow menu */}
      <Menu
        anchorEl={verMenuAnchor}
        open={verMenuAnchor !== null}
        onClose={() => {
          setVerMenuAnchor(null);
          setVerMenuTarget(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setVersionDialog({ version: verMenuTarget });
            setVerMenuAnchor(null);
          }}
        >
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={updateVersion.isPending}
          onClick={() => {
            const v = verMenuTarget;
            setVerMenuAnchor(null);
            if (!v) return;
            updateVersion.mutate({
              versionNumber: v.versionNumber,
              body: { isActive: !v.isActive },
            });
          }}
        >
          <ListItemText>
            {verMenuTarget?.isActive ? "Deactivate" : "Activate"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteVersion(verMenuTarget);
            setVerMenuAnchor(null);
          }}
        >
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Class overflow menu */}
      <Menu
        anchorEl={clsMenuAnchor}
        open={clsMenuAnchor !== null}
        onClose={() => {
          setClsMenuAnchor(null);
          setClsMenuTarget(null);
        }}
      >
        <MenuItem
          onClick={() => {
            setClassDialog({ klass: clsMenuTarget });
            setClsMenuAnchor(null);
          }}
        >
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteClass(clsMenuTarget);
            setClsMenuAnchor(null);
          }}
        >
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      {editModelOpen ? (
        <EditModelDialog
          open
          model={model}
          onClose={() => {
            setEditModelOpen(false);
          }}
          onSaved={() => {
            setEditModelOpen(false);
          }}
        />
      ) : null}

      {deleteModelOpen ? (
        <ConfirmDeleteDialog
          open
          title="Delete AI model"
          message={`Delete model "${model.modelName}"? This is a soft delete.`}
          pending={deleteModel.isPending}
          onClose={() => {
            setDeleteModelOpen(false);
          }}
          onConfirm={() => {
            deleteModel.mutate(model.modelId, {
              onSuccess: () => {
                void navigate("/ai-models");
              },
            });
          }}
        />
      ) : null}

      {versionDialog ? (
        <VersionDialog
          open
          modelId={modelId}
          version={versionDialog.version}
          nextVersionNumber={nextVersionNumber}
          onClose={() => {
            setVersionDialog(null);
          }}
          onSaved={() => {
            setVersionDialog(null);
          }}
        />
      ) : null}

      {deleteVersion ? (
        <ConfirmDeleteDialog
          open
          title="Delete version"
          message={`Delete version ${String(deleteVersion.versionNumber)}? This is a soft delete.`}
          pending={deleteVersionMut.isPending}
          onClose={() => {
            setDeleteVersion(null);
          }}
          onConfirm={() => {
            deleteVersionMut.mutate(deleteVersion.versionNumber, {
              onSuccess: () => {
                setDeleteVersion(null);
              },
            });
          }}
        />
      ) : null}

      {classDialog ? (
        <ClassDialog
          open
          modelId={modelId}
          klass={classDialog.klass}
          nextIndex={nextClassIndex}
          onClose={() => {
            setClassDialog(null);
          }}
          onSaved={() => {
            setClassDialog(null);
          }}
        />
      ) : null}

      {deleteClass ? (
        <ConfirmDeleteDialog
          open
          title="Delete class"
          message={`Delete class "${deleteClass.className}" (index ${String(deleteClass.classIndex)})? This is a soft delete; the class_uuid stays stable.`}
          pending={deleteClassMut.isPending}
          onClose={() => {
            setDeleteClass(null);
          }}
          onConfirm={() => {
            deleteClassMut.mutate(deleteClass.classUuid, {
              onSuccess: () => {
                setDeleteClass(null);
              },
            });
          }}
        />
      ) : null}

      {importOpen ? (
        <ImportClassesDialog
          open
          modelId={modelId}
          modelName={model.modelName}
          existing={classes}
          onClose={() => {
            setImportOpen(false);
          }}
          onImported={() => {
            /* keep dialog open to show server counts */
          }}
        />
      ) : null}
    </Box>
  );
}
